// src/components/page-sections/RichTextSection.js
import parse from 'html-react-parser';
import React, { useEffect, useRef } from 'react';

const RichTextSection = ({ content }) => {
  const sectionRef = useRef(null);

  const {
    richText,
    alignment = 'left',
    backgroundColor = '',
    textColor = '',
    customBackgroundColor,
    customTextColor,
    padding,
    margin,
    animation = '',
    animationDelay = 0,
    animationDuration = 1000,
    shadow,
    borderRadius,
    borderWidth,
    borderStyle,
    borderColor,
    maxWidth
  } = content;

  // Set up animations when the section comes into view
  useEffect(() => {
    if (!animation || !sectionRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;

          // Add animation class based on the selected animation
          switch (animation) {
            case 'fade-in':
              element.classList.add('animate-fade-in');
              break;
            case 'slide-up':
              element.classList.add('animate-slide-up');
              break;
            case 'slide-in-left':
              element.classList.add('animate-slide-in-left');
              break;
            case 'slide-in-right':
              element.classList.add('animate-slide-in-right');
              break;
            case 'zoom-in':
              element.classList.add('animate-zoom-in');
              break;
            default:
              break;
          }

          // Stop observing after animation is triggered
          observer.unobserve(element);
        }
      });
    }, { threshold: 0.1 });

    observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [animation]);

  // Handle alignment
  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  // Prepare styles
  const sectionStyle = {};

  // Background Color
  if (backgroundColor === 'custom' && customBackgroundColor) {
    sectionStyle.backgroundColor = customBackgroundColor;
  }

  // Text Color
  if (textColor === 'custom' && customTextColor) {
    sectionStyle.color = customTextColor;
  }

  // Add border if all border properties are provided
  if (borderWidth && borderStyle && borderColor) {
    sectionStyle.borderWidth = borderWidth;
    sectionStyle.borderStyle = borderStyle;
    sectionStyle.borderColor = borderColor;
  }

  // Add other style properties if provided
  if (padding) sectionStyle.padding = padding;
  if (margin) sectionStyle.margin = margin;
  if (borderRadius) sectionStyle.borderRadius = borderRadius;
  if (maxWidth) sectionStyle.maxWidth = maxWidth;

  // For animation delay and duration
  if (animation && animationDelay) {
    sectionStyle.animationDelay = `${animationDelay}ms`;
  }
  if (animation && animationDuration !== 1000) {
    sectionStyle.animationDuration = `${animationDuration}ms`;
  }

  // Shadow class
  let shadowClass = '';
  if (shadow === 'sm') shadowClass = 'shadow-sm';
  else if (shadow === 'md') shadowClass = 'shadow';
  else if (shadow === 'lg') shadowClass = 'shadow-lg';
  else if (shadow === 'xl') shadowClass = 'shadow-xl';

  // Container class combining alignment and shadow
  const containerClasses = `${alignmentClasses[alignment]} ${shadowClass} ${backgroundColor !== 'custom' ? backgroundColor : ''} ${textColor !== 'custom' ? textColor : ''}`;

  return (
    <div
      ref={sectionRef}
      className={`rich-text-section ${containerClasses}`}
      style={sectionStyle}
    >
      <div className="rich-text-content">
        {richText ? parse(richText) : <p>No content provided</p>}
      </div>

      <style jsx>{`
        .rich-text-content {
          width: 100%;
          ${maxWidth ? `max-width: ${maxWidth}; margin-left: auto; margin-right: auto;` : ''}
        }
        
        .rich-text-content img {
          max-width: 100%;
          height: auto;
        }
        
        .rich-text-content h1 {
          font-size: 2.5rem;
          font-weight: bold;
          margin-bottom: 1rem;
        }
        
        .rich-text-content h2 {
          font-size: 2rem;
          font-weight: bold;
          margin-bottom: 0.75rem;
        }
        
        .rich-text-content h3 {
          font-size: 1.75rem;
          font-weight: bold;
          margin-bottom: 0.75rem;
        }
        
        .rich-text-content h4 {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
        }
        
        .rich-text-content p {
          margin-bottom: 1rem;
        }
        
        .rich-text-content ul, .rich-text-content ol {
          margin-bottom: 1rem;
          padding-left: 2rem;
        }
        
        .rich-text-content ul {
          list-style-type: disc;
        }
        
        .rich-text-content ol {
          list-style-type: decimal;
        }
        
        .rich-text-content blockquote {
          border-left: 4px solid #e2e8f0;
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          margin-bottom: 1rem;
          font-style: italic;
        }
        
        .rich-text-content table {
          border-collapse: collapse;
          width: 100%;
          margin-bottom: 1rem;
        }
        
        .rich-text-content th, .rich-text-content td {
          border: 1px solid #e2e8f0;
          padding: 0.5rem;
        }
        
        .rich-text-content th {
          background-color: #f7fafc;
          font-weight: bold;
        }
        
        .rich-text-content a {
          color: #3182ce;
          text-decoration: underline;
        }
        
        .rich-text-content a:hover {
          text-decoration: none;
        }
        
        .rich-text-content .video-container {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
          overflow: hidden;
          max-width: 100%;
          margin: 1rem 0;
        }
        
        .rich-text-content .video-container iframe,
        .rich-text-content .video-container video {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        
        /* Animation classes */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { transform: translateY(50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes slideInLeft {
          from { transform: translateX(-100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes slideInRight {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes zoomIn {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .animate-fade-in {
          animation: fadeIn 1s ease-out forwards;
        }

        .animate-slide-up {
          animation: slideUp 0.8s ease-out forwards;
        }

        .animate-slide-in-left {
          animation: slideInLeft 0.8s ease-out forwards;
        }

        .animate-slide-in-right {
          animation: slideInRight 0.8s ease-out forwards;
        }

        .animate-zoom-in {
          animation: zoomIn 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default RichTextSection;